import type { CompareData } from '../Compare.types';

type Translate = (key: string) => string;

export const getOpenCorporatesCompareData = (t: Translate): CompareData => ({
  competitor: 'OpenCorporates',
  competitorHeader: 'OpenCorporates',
  competitorLogo: { type: 'logo-opencorporates', label: 'OpenCorporates' },
  heroTitle: 'Global Database vs OpenCorporates',
  heroDescription: (
    <>
      {t('We audited both platforms across 174 jurisdictions, page by page. The result:')}{' '}
      <b>{t("61 of OpenCorporates' 147 jurisdictions are currently sitting on offline registry feeds")}</b>{' '}
      {t(
        "— some for over a decade — while Global Database publishes a refresh schedule for every territory it covers. Here's the full comparison, sourced and cited.",
      )}
    </>
  ),
  heroActions: { primaryLabel: t('Request a data review'), secondaryLabel: t('See the freshness audit') },
  headStats: [
    { value: '600M+', label: t('Global Database companies') },
    { value: '220M+', label: t('OpenCorporates companies'), muted: true },
  ],
  quickItems: [
    <>
      <b>{t('Every jurisdiction on a published refresh schedule')}</b> {t('— real-time, daily, weekly or monthly')}
    </>,
    <>
      <b>{t('Financials digitized from PDF filings')}</b> {t('— 20 years of history, including private companies')}
    </>,
    <>
      <b>{t('Global ownership graph')}</b> {t('connecting people to companies, plus PEPs & sanctions screening')}
    </>,
    <>
      <b>{t('Built for AI workflows')}</b>{' '}
      {t('— MCP server, Agentic API, and native connectors for Claude, ChatGPT and Microsoft Copilot')}
    </>,
  ],

  table: {
    title: t('Global Database vs OpenCorporates at a glance'),
    description: t(
      'Both source company records from official government registries. Where they diverge is what happens after the raw filing — enrichment, ownership resolution, refresh discipline, and how AI-era access is priced.',
    ),
    rows: [
      {
        attribute: t('Coverage'),
        gd: t('600M+ companies (684.9M audited across 174 territories)'),
        them: t('220M+ companies (234.3M across 147 jurisdictions)'),
      },
      {
        attribute: t('Registry freshness'),
        gd: t('Every territory on a published schedule: real-time, daily, weekly or monthly'),
        them: t('61 of 147 jurisdictions currently offline on both feeds — some for 10+ years'),
      },
      {
        attribute: t('Financials'),
        gd: t('Digitized from PDF/scanned filings, 20 years of history, including private/SME companies'),
        them: t('Raw filings only, where the registry itself publishes them — no digitization'),
      },
      {
        attribute: t('Ownership & UBO'),
        gd: t('Global ownership graph connecting people to companies across jurisdictions'),
        them: t('Direct parent-subsidiary links only, where explicitly filed; no cross-border graph'),
      },
      {
        attribute: t('Compliance data'),
        gd: t('PEPs and sanctions screening included'),
        them: t('Not provided'),
      },
      {
        attribute: t('Firmographic enrichment'),
        gd: t('Websites, descriptions, social profiles, industry tags, technology stack'),
        them: t('Basic registry attributes only'),
      },
      {
        attribute: t('AI & agentic access'),
        gd: t('MCP server, Agentic API, native connectors for Claude, ChatGPT, Microsoft Copilot'),
        them: t('Standard REST API only'),
      },
      {
        attribute: t('Coverage growth'),
        gd: t('3–5 new jurisdictions added per month'),
        them: t('Jurisdiction list has been largely static; many feeds frozen'),
      },
      {
        attribute: t('API pricing'),
        gd: t('Free searches — pay only for full profiles or enriched fields; up to 6,000 requests/minute'),
        them: t('Credit-based; a basic search consumes a credit. 500 calls/month cap on standard plans'),
      },
    ],
  },

  stats: {
    title: t('The freshness audit: 61 of 147 jurisdictions, offline'),
    description: t(
      'In April 2026, we checked every OpenCorporates jurisdiction page directly against its own published "Data last updated" field, and cross-referenced it with Global Database\'s own published refresh schedule.',
    ),
    items: [
      { value: '2.92x', label: t('Coverage ratio, GDB vs OC') },
      { value: '61/147', label: t('OC jurisdictions offline') },
      { value: '23.7%', label: t("Of OC's universe sits offline") },
      { value: '16', label: t('OC jurisdictions stale 10+ years') },
    ],
    staleTable: {
      intro: t(
        "A sample of jurisdictions where OpenCorporates' own knowledge base shows no update in years — compared against Global Database's published refresh frequency for the same territory:",
      ),
      columns: [t('Jurisdiction'), t('OC Last Updated'), t('Years Stale'), t('GDB Refresh')],
      rows: [
        { jurisdiction: 'Spain', updated: 'August 2011', stale: '14.7 yrs', refresh: t('Daily') },
        { jurisdiction: 'Poland', updated: 'February 2013', stale: '13.2 yrs', refresh: t('Daily') },
        { jurisdiction: 'UAE — Dubai', updated: 'March 2014', stale: '12.1 yrs', refresh: t('Weekly') },
        { jurisdiction: 'South Africa', updated: 'September 2014', stale: '11.6 yrs', refresh: t('Weekly') },
        { jurisdiction: 'Mexico', updated: 'January 2015', stale: '11.3 yrs', refresh: t('Daily') },
        { jurisdiction: 'Brazil', updated: 'August 2018', stale: '7.7 yrs', refresh: t('Daily') },
      ],
    },
  },

  difference: {
    title: t("What's the difference between Global Database and OpenCorporates?"),
    paragraphs: [
      <>
        {t('OpenCorporates and Global Database both start from the same place: official company registries.')}{' '}
        <b>OpenCorporates</b>{' '}
        {t(
          'built its reputation on open access — aggregating filings from 147 jurisdictions into roughly 220M+ freely searchable company records. Every freshness status is openly published on its own knowledge base, which is exactly how we were able to audit it.',
        )}
      </>,
      <>
        {t('The gap is in what that openness reveals:')}{' '}
        <b>{t('61 of those 147 jurisdictions are currently sitting on frozen feeds')}</b>{' '}
        {t(
          "— for 16 of them, the last recorded update is more than a decade old. That's not a hidden flaw; it's disclosed. It's just rarely part of a standard procurement check, since most buyers evaluate coverage breadth and per-record pricing, not the per-jurisdiction freshness file.",
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          'takes the opposite trade-off: rather than maximizing the jurisdiction count, engineering capacity goes toward keeping the 174+ territories already covered on a published, verifiable refresh schedule — daily for the large majority, real-time for the UK and Germany. On top of that registry layer, every profile is enriched with digitized financials, a global ownership graph, PEPs and sanctions data, and firmographic context that a raw registry filing never contains.',
        )}
      </>,
      <>
        {t(
          "Neither platform is wrong for its use case. A one-off name-and-status check in a jurisdiction OpenCorporates actively maintains is exactly what its open model is for. Where ownership resolution, financial depth, compliance screening, or AI-native access is part of the job, that's the gap Global Database is built to close.",
        )}
      </>,
    ],
    pull: t(
      '"The 174 territories already in our coverage are kept on the published refresh schedule. That commitment is what produces the freshness profile: 104 territories on a daily schedule, 7 on weekly, 5 on monthly, and 2 — the UK and Germany — on real-time API connections."',
    ),
  },

  features: {
    title: t('Key features compared'),
    description: t("A closer look at what's included on each platform, beyond the headline registry search."),
    gd: [
      {
        title: t('Digitized financials, 20 years'),
        badge: t("Includes private co's"),
        text: t(
          'Financial statements extracted from PDF and scanned filings into structured, comparable data — including SMEs with no public listing.',
        ),
      },
      {
        title: t('Global ownership graph'),
        text: t(
          "Connects individuals to companies across jurisdictions, not just within a single registry's own filing.",
        ),
      },
      {
        title: t('PEPs & sanctions screening'),
        badge: t('New'),
        text: t('Politically exposed persons and sanctions list data included alongside ownership records.'),
      },
      {
        title: t('MCP server & AI connectors'),
        badge: t('New'),
        text: t(
          'Native integration for Claude, ChatGPT and Microsoft Copilot, plus an Agentic API for autonomous workflows.',
        ),
      },
      {
        title: t('Firmographic enrichment'),
        text: t(
          'Websites, descriptions, social profiles, industry tags and technology stacks layered on registry data.',
        ),
      },
      {
        title: t('Growing coverage'),
        text: t(
          '3–5 new jurisdictions added per month, each held to the same refresh-reliability standard as existing coverage.',
        ),
      },
      {
        title: t('Free search, pay for depth'),
        text: t(
          'No charge to search; pricing applies to full profiles and enriched fields, at up to 6,000 requests/minute.',
        ),
      },
    ],
    them: [
      {
        title: t('Open registry search'),
        text: t("Free-text search across 147 jurisdictions' company registers, ~220M+ companies."),
      },
      {
        title: t('Registered officers'),
        text: t('Officer and direct ownership data as filed, where the registry publishes it.'),
      },
      {
        title: t('Published freshness status'),
        text: t(
          "Every jurisdiction's update status is transparently listed — which is how this comparison was possible.",
        ),
      },
      {
        title: t('PDF-only financials'),
        text: t('Statements available only in original filed format, where the registry provides them at all.'),
      },
      {
        title: t('Standard REST API'),
        text: t(
          '500 calls/month, 200/day cap on standard plans; a fixed ~£2,250/year subscription regardless of usage.',
        ),
      },
      {
        title: t('Credit-per-search pricing'),
        text: t('A basic search consumes a credit the same as retrieving a full record.'),
      },
      {
        title: t('No compliance layer'),
        text: t("No PEPs, sanctions, or cross-border ownership graph beyond what's directly filed."),
      },
    ],
  },

  extra: {
    id: 'api',
    title: t('API access & pricing'),
    description: t(
      'For teams integrating company data into a product, CRM, or AI workflow, this is often the deciding factor.',
    ),
    paragraphs: [
      <>
        <b>OpenCorporates</b>{' '}
        {t(
          'runs a credit-based model where a basic search consumes a credit — the same cost whether or not it returns a usable record. Standard plans cap out at 500 calls a month (200/day), for a fixed annual fee regardless of actual usage.',
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          'separates search from retrieval: searching is free at up to 6,000 requests per minute, and cost applies only when a full profile or enriched field is retrieved. For AI-driven workflows specifically, data is also available through an',
        )}{' '}
        <b>MCP server</b> {t('and an')} <b>Agentic API</b>
        {t(', with native connectors already built for')} <b>Claude, ChatGPT, and Microsoft Copilot</b>{' '}
        {t('— so an AI agent can query company data directly, not through a scraped intermediary.')}
      </>,
    ],
  },

  faq: {
    title: t('FAQs about Global Database vs OpenCorporates'),
    data: [
      {
        id: 'best-opencorporates-alternative',
        default_opened: true,
        question: t('What is the best OpenCorporates alternative?'),
        answer: t(
          "For teams that need more than a name-and-status lookup — ownership resolution, digitized financials, compliance screening, or AI-native access — Global Database is commonly evaluated as the practical alternative. It covers 600M+ companies across 174+ territories, each on a published refresh schedule, versus OpenCorporates' 220M+ where 61 of 147 jurisdictions are currently offline.",
        ),
      },
      {
        id: 'is-opencorporates-up-to-date',
        question: t('Is OpenCorporates data up to date?'),
        answer: t(
          'It depends heavily on the jurisdiction. Our April 2026 audit of all 147 OpenCorporates jurisdictions found 61 currently sitting on offline feeds — 16 of them not updated in more than 10 years, including Spain (14.7 years) and Poland (13.2 years). The other 86 jurisdictions, including most major Western economies, are actively refreshed. <a href="https://www.globaldatabase.com/company-data-coverage-by-country-a-174-jurisdiction-comparison-of-global-database-and-opencorporates">See the full jurisdiction-by-jurisdiction audit →</a>',
        ),
      },
      {
        id: 'does-opencorporates-provide-ubo-peps',
        question: t('Does OpenCorporates provide UBO, PEPs, or sanctions data?'),
        answer: t(
          'No. OpenCorporates publishes direct ownership links as filed with each registry, but does not build a cross-border ownership graph, and does not include PEPs or sanctions screening. Global Database includes both as standard.',
        ),
      },
      {
        id: 'can-i-use-data-with-ai-tools',
        question: t('Can I use OpenCorporates or Global Database data with AI tools like Claude or ChatGPT?'),
        answer: t(
          'Global Database is built for this directly — an MCP server and Agentic API with native connectors for Claude, ChatGPT and Microsoft Copilot, so an AI agent can query live company data as part of its workflow. OpenCorporates offers a standard REST API without dedicated AI-agent tooling.',
        ),
      },
      {
        id: 'how-does-api-pricing-compare',
        question: t("How does OpenCorporates' API pricing compare to Global Database?"),
        answer: t(
          'OpenCorporates uses a credit-based model where every search — successful or not — consumes a credit, capped at 500 calls/month on standard plans for a fixed annual fee. Global Database makes search free and charges only for retrieving full profiles or enriched fields, supporting up to 6,000 requests per minute.',
        ),
      },
      {
        id: 'financial-data-for-private-companies',
        question: t('Does Global Database have financial data for private companies?'),
        answer: t(
          "Yes. Financial statements are digitized directly from filed PDFs and scanned documents, covering up to 20 years of history — including private and SME companies that don't have public financial disclosures elsewhere.",
        ),
      },
      {
        id: 'how-often-new-countries',
        question: t('How often does Global Database add new countries?'),
        answer: t(
          "Global Database adds 3–5 new jurisdictions per month. New territories are added once the underlying registry connection meets the same reliability standard as existing coverage — typically when a government registry opens a stable API or bulk-data export — rather than adding a nominal connection that can't be kept current.",
        ),
      },
      {
        id: 'better-for-compliance-teams',
        question: t('Is Global Database better than OpenCorporates for compliance teams?'),
        answer: t(
          'For compliance and risk teams specifically, yes in most evaluations — Global Database adds a global ownership graph, PEPs and sanctions screening, and digitized financials that OpenCorporates does not include, on top of a published, verifiable refresh schedule across every covered territory.',
        ),
      },
    ],
  },

  cta: {
    title: t('See Global Database on your own company records'),
    description: t(
      "Request a data review and compare match rates, ownership resolution and freshness directly against what you're using today.",
    ),
  },
});
